/**
 * OMON-137 Phase 2 → ADR-0045 §6: Users & Roles — ONE row per person.
 *
 * Until 2026-09-17 this rendered every `_practitionerRole` doc as a row, so it
 * listed production role grants only: anyone who held access solely in a
 * non-production environment (an invited consultant, a staff member added to
 * demo) was invisible here, and "everyone I have created" lived in two places.
 * Now each row is a PERSON from the unified catalogue (`./user-catalogue`):
 * their production role grants AND their non-production accounts, wherever
 * they came from — OneTouch sync, manual creation, or the Add user door.
 *
 * Accounts are still separate per environment (ADR-0045 — a training login
 * cannot exist in production); only this view merges them, joining the guest
 * list to production people by email.
 *
 * Interaction, one shape for every row: the row opens the PERSON CARD
 * (ADR-0046 §5, amended 2026-09-22), which replaced the two dialogs a row used
 * to lead to (name → identity, row → access). The card's Edit buttons hand
 * over to those editors until per-panel editing lands. A production chip is a
 * shortcut straight to that grant's editor. No inline buttons.
 *
 * Refresh semantics: re-fetches on every panel entry and after every save.
 * Filtering (search + environment) is client-side over the loaded rows.
 */
import { adminUsersRolesList } from "../dom";
import { ensurePatients } from "../loaders/ensure";
import { state } from "../state";
import { escapeHtml } from "../utils/html";
import { firestoreDatabaseId } from "../firebase";
import {
  loadPersonSummaries,
  loadPractitionerRoles,
  type PersonProvenance,
  type PractitionerRoleRow,
} from "./loaders";
import type { AccessRole } from "./claims";
import { openEditPractitionerRoleModal } from "./edit-practitioner-role";
import { closePersonCard, openPersonCard } from "./person-card-ui";
import { openAddUserModal } from "./add-user";
import { roleLabel, roleName } from "./access-labels";
import { ROLE_STATUS_LABEL } from "./role-status";
import { callEnvironmentInviteAction, callRelatedPersonAction } from "./cf-client";
import {
  CLIENT_FILTER,
  FAMILY_FILTER,
  PRODUCTION,
  buildCatalogue,
  familyNames,
  matchesCatalogueSearch,
  matchesEnvironment,
  sharesAddress,
  whyListed,
  summarise,
  type CatalogueRow,
  type ClientInfo,
  type EnvironmentAccess,
  type EnvironmentEntry,
  type FamilyLink,
} from "./user-catalogue";

const ROLE_DISPLAY: Record<AccessRole, string> = {
  carer: "Carer",
  planner: "Planner",
  librarian: "Librarian",
  admin: "Admin",
  auditor: "Auditor",
  "practice-author": "Practice author",
};

const ACCESS_STATUS_LABEL = { active: "Active", revoked: "Revoked", expired: "Expired" } as const;

function envLabel(env: string): string {
  return env.charAt(0).toUpperCase() + env.slice(1);
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/** ADR-0046 phase 2 (read side): origin as a quiet pill, a loud one only
 *  when the sync has flagged divergence. Information, not controls. */
const ORIGIN_LABEL: Record<string, string> = {
  onetouch: "OneTouch",
  stc: "STC",
  guest: "Guest",
};

/**
 * WHY this person is in the list (Ed, 2026-09-21) — "OneTouch staff",
 * "Invited to training", "Family of Mum · Dad", or several at once. It
 * REPLACES the bare origin chip ("OneTouch" / "STC"), which said where the
 * record came from but not why the person is here; the steward detail that
 * chip's tooltip carried moves to this line's tooltip.
 */
function whyLine(row: CatalogueRow, prov: PersonProvenance | null): string {
  const why = whyListed(row);
  if (why.length === 0) return "";
  const stcDomains = Object.entries(prov?.stewards ?? {})
    .filter(([, who]) => who === "stc").map(([d]) => d);
  const title = "Why they are in this list" +
    (prov ? ` · record authored by ${ORIGIN_LABEL[prov.origin] ?? prov.origin}` : "") +
    (stcDomains.length ? ` · STC stewards: ${stcDomains.join(", ")}` : "");
  return `<span class="scope-badge" title="${escapeHtml(title)}">${escapeHtml(why.join(" · "))}</span>`;
}

function provenanceChips(prov: PersonProvenance | null): string {
  if (!prov) return "";
  // The origin now reads as part of whyLine(); only the divergence warning
  // (ADR-0046) stays here.
  let chips = "";
  const diverged = prov.diverged?.fields ?? [];
  if (diverged.length) {
    chips += ` <span class="status-badge status-suspended" title="OneTouch disagrees about STC-stewarded field(s); flagged by the sync, not overwritten (ADR-0046)">diverged: ${escapeHtml(diverged.join(", "))}</span>`;
  }
  return chips;
}

/** One production grant as a clickable chip: role, and status when it is not
 *  simply in effect. The chip opens that grant's edit modal. */
/**
 * Every chip reads the same way: ENVIRONMENT · ROLE, then only what is true
 * and not the default — the status when it is not simply in effect, an expiry
 * when the access actually lapses, the data scope on production. "Open-ended"
 * was dropped: it is what most grants are (Ed, 2026-09-18).
 */
function accessChip(opts: {
  environment: string;
  role: string;
  inEffect: boolean;
  statusText: string;
  suffix: string;
  title: string;
  roleClass?: string;
  roleId?: string;
}): string {
  const cls = opts.inEffect
    ? (opts.roleClass ?? "scope-badge scope-both")
    : "status-badge status-suspended";
  const link = opts.roleId ? ` role-chip-link" data-role-id="${escapeHtml(opts.roleId)}` : "";
  const text = `${envLabel(opts.environment)} · ${opts.role}${opts.statusText}${opts.suffix}`;
  return `<span class="${cls}${link}" title="${escapeHtml(opts.title)}">${escapeHtml(text)}</span>`;
}

function productionChip(r: PractitionerRoleRow): string {
  return accessChip({
    environment: PRODUCTION,
    role: r.role ? ROLE_DISPLAY[r.role] ?? r.role : "?",
    inEffect: r.status === "active",
    statusText: r.status === "active" ? "" : ` · ${ROLE_STATUS_LABEL[r.status]}`,
    suffix: r.dataScope ? ` · data ${r.dataScope}` : "",
    title: `Production role grant ${r.roleId} — click to edit`,
    roleClass: `role-badge role-${r.role ?? "unknown"}`,
    roleId: r.roleId,
  });
}

function environmentChip(e: EnvironmentAccess): string {
  return accessChip({
    environment: e.environment,
    role: roleName(e.role),
    inEffect: e.status === "active",
    statusText: e.status === "active" ? "" : ` · ${ACCESS_STATUS_LABEL[e.status]}`,
    // Only a dated grant says when it ends; an open-ended one says nothing.
    suffix: e.expires ? ` · until ${e.expires}` : "",
    title: `${roleLabel(e.role)} in the ${e.environment} environment — open the row to change it`,
  });
}

/** OMON-348: one Circle of Support chip per client this person is family of.
 *  Access belongs to the relationship, so the client is named on the chip. */
const FAMILY_STATE_TEXT: Record<FamilyLink["accessState"], string> = {
  "using": "using the app",
  "granted": "access given",
  "not-granted": "no access",
  "requested": "asked to join",
  "revoked": "access removed",
};

function familyChip(l: FamilyLink): string {
  const inEffect = l.accessState === "using" || l.accessState === "granted";
  const cls = inEffect ? "scope-badge scope-both" : "status-badge status-suspended";
  const client = l.clientName ?? "a client";
  const text = `Circle of Support · ${client} · ${FAMILY_STATE_TEXT[l.accessState]}`;
  return `<span class="${cls}" title="Family contact of ${escapeHtml(client)} — open the row to change access">${escapeHtml(text)}</span>`;
}

/** ADR-0046 §6: a client's one chip — who can see them in Circle of Support. */
function clientChip(row: CatalogueRow): string {
  const circle = row.client?.circle ?? [];
  const withAccess = circle.filter((l) => l.accessState === "using" || l.accessState === "granted").length;
  const text = circle.length === 0
    ? "Circle of Support · no family on file"
    : `Circle of Support · ${withAccess} of ${circle.length} family with access`;
  const cls = withAccess > 0 ? "scope-badge scope-both" : "scope-badge";
  return `<span class="${cls}" title="Family contacts of this client, and how many can see them in Circle of Support">${escapeHtml(text)}</span>`;
}

function renderPersonRow(row: CatalogueRow): string {
  // ☠️ OMON-348: one row per email. When several people share the address,
  // name them all — whoever holds that inbox can sign in as any of them.
  const displayName = sharesAddress(row)
    ? familyNames(row).join(" & ")
    : (row.name ?? row.email ?? row.personDocId ?? "unknown");
  // ADR-0046 §5 (2026-09-22): the whole row opens the person card, so the
  // name no longer needs a separate link to a different dialog.
  const title = escapeHtml(displayName);
  const prov = row.personDocId ? (_personProvenance.get(row.personDocId) ?? null) : null;
  // A family contact comes from the live system, so it is never "outside
  // production only" (OMON-348).
  const onlyOutside = row.productionRoles.length === 0 && row.family.length === 0 && !row.client
    ? `<span class="scope-badge" title="No production access — this person exists only in the environment(s) shown">Outside production only</span>`
    : "";
  const sharedAddress = sharesAddress(row)
    ? `<span class="status-badge status-suspended" title="These people are on file with the SAME email address. With email sign-in, whoever reads that inbox can sign in as any of them.">⚠ ${familyNames(row).length} people share this address</span>`
    : "";
  const emailLine = row.email && row.name ? `<span>${escapeHtml(row.email)}</span>` : "";
  const chips = [
    ...row.productionRoles.map(productionChip),
    ...row.environments.map(environmentChip),
    ...row.family.map(familyChip),
    ...(row.client ? [clientChip(row)] : []),
  ].join(" ");
  // A client row is never "soft-deleted" looking: clients don't hold access,
  // they are who access is TO.
  const inEffect = !!row.client || row.productionRoles.some((r) => !r.inactive) ||
    row.environments.some((e) => e.status === "active") ||
    row.family.some((l) => l.accessState === "using" || l.accessState === "granted");
  // ADR-0045 §6 write side: EVERY row opens the dialog, including someone
  // with no production grant — their environment access is editable there.
  const clickable = " admin-row-clickable";
  const roleAttr = ` data-person-key="${escapeHtml(row.key)}"`;
  const affordance = `<div class="admin-row-affordance" aria-hidden="true">›</div>`;
  return `
    <div class="admin-row${clickable}${inEffect ? "" : " soft-deleted"}"${roleAttr}>
      <div class="admin-row-main">
        <div class="admin-row-title">${title}</div>
        <div class="admin-row-meta">
          ${whyLine(row, prov)}
          ${chips}
          ${provenanceChips(prov)}
          ${onlyOutside}
          ${sharedAddress}
          ${emailLine}
        </div>
      </div>
      ${affordance}
    </div>
  `;
}

// Module-level state for the panel's lifecycle. render() replaces it; the
// click, search and filter handlers read it.
let _roleRows: PractitionerRoleRow[] = [];
let _catalogue: CatalogueRow[] = [];
/** Keys of the rows currently shown, in order — the card's Previous / Next. */
let _visibleKeys: string[] = [];
let _searchQuery = "";
let _environmentFilter = "all";
let _personProvenance = new Map<string, PersonProvenance | null>();
let _environmentsNote = "";
let _familyNote = "";
let _clientsNote = "";

function filterOptions(): string {
  const envs = new Set<string>();
  for (const row of _catalogue) for (const e of row.environments) envs.add(e.environment);
  const options: Array<[string, string]> = [
    ["all", "All environments"],
    [PRODUCTION, "Production"],
    ...Array.from(envs).sort().map((e) => [e, envLabel(e)] as [string, string]),
    ["non-production-only", "Outside production only"],
    [FAMILY_FILTER, "Family contacts (Circle of Support)"],
    [CLIENT_FILTER, "Clients"],
  ];
  return options
    .map(([v, label]) => `<option value="${escapeHtml(v)}"${v === _environmentFilter ? " selected" : ""}>${escapeHtml(label)}</option>`)
    .join("");
}

function renderPanelBody(): void {
  const filtered = _catalogue.filter(
    (r) => matchesEnvironment(r, _environmentFilter) && matchesCatalogueSearch(r, _searchQuery),
  );
  // The person card's Previous / Next walk exactly this list.
  _visibleKeys = filtered.map((r) => r.key);
  const s = summarise(filtered);
  const envCounts = Object.entries(s.activeByEnvironment)
    .sort(([a], [b]) => (a === PRODUCTION ? -1 : b === PRODUCTION ? 1 : a.localeCompare(b)))
    .map(([env, n]) => `<span>${escapeHtml(envLabel(env))}: ${n} with access</span>`)
    .join("");
  const narrowed = _searchQuery || _environmentFilter !== "all";

  const summary = `
    <div class="admin-row admin-row-summary">
      <div class="admin-row-main">
        <div class="admin-row-title">
          ${s.people} ${s.people === 1 ? "person" : "people"}
          ${narrowed ? `<span class="admin-search-of">(of ${_catalogue.length})</span>` : ""}
        </div>
        <div class="admin-row-meta">
          <span>${s.productionPeople} in production</span>
          <span>${s.nonProductionOnly} outside production only</span>
          <span>${s.familyPeople} family contacts · ${s.familyWithAccess} with Circle of Support</span>
          <span>${s.clients} clients · ${s.clientsWithCircle} with someone in Circle of Support</span>
          ${envCounts}
          ${_environmentsNote ? `<span>${escapeHtml(_environmentsNote)}</span>` : ""}
          ${_familyNote ? `<span>${escapeHtml(_familyNote)}</span>` : ""}
          ${_clientsNote ? `<span>${escapeHtml(_clientsNote)}</span>` : ""}
        </div>
      </div>
    </div>
  `;

  const rowsHtml =
    filtered.length === 0
      ? `<div class="admin-empty">${narrowed ? "Nobody matches the search and environment filter." : "No users yet."}</div>`
      : filtered.map(renderPersonRow).join("");

  // The shell is rendered once; only the body re-renders on search, filter
  // or save, which keeps the search input's focus and cursor.
  let bodyEl = adminUsersRolesList.querySelector<HTMLElement>(".admin-users-roles-body");
  if (!bodyEl) {
    adminUsersRolesList.innerHTML = `
      <div class="admin-users-roles-toolbar">
        <input
          type="search"
          id="adminUsersRolesSearch"
          class="search-bar"
          placeholder="Search by name, email, role or environment…"
          autocomplete="off"
          spellcheck="false"
        />
        <select id="adminUsersEnvironmentFilter" class="search-bar admin-users-environment-filter" aria-label="Show people with access in"></select>
        <button class="btn-outline" id="adminAddUser" title="Add someone who didn't come from the OneTouch sync — environment and role in one step">+ Add user</button>
      </div>
      <div class="admin-users-roles-body"></div>
    `;
    bodyEl = adminUsersRolesList.querySelector<HTMLElement>(".admin-users-roles-body")!;
    wireToolbar();
  }
  const select = adminUsersRolesList.querySelector<HTMLSelectElement>("#adminUsersEnvironmentFilter");
  if (select) select.innerHTML = filterOptions();
  bodyEl.innerHTML = summary + rowsHtml;
  wireRowClicks(bodyEl);
}

function wireToolbar(): void {
  const input = adminUsersRolesList.querySelector<HTMLInputElement>("#adminUsersRolesSearch");
  if (input) {
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    input.addEventListener("input", () => {
      if (debounceTimer !== null) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        _searchQuery = input.value.trim();
        renderPanelBody();
      }, 120);
    });
    // Restore the query when the panel is left and re-entered.
    if (_searchQuery !== "") input.value = _searchQuery;
  }
  adminUsersRolesList
    .querySelector<HTMLSelectElement>("#adminUsersEnvironmentFilter")
    ?.addEventListener("change", (ev) => {
      _environmentFilter = (ev.target as HTMLSelectElement).value;
      renderPanelBody();
    });
  // One door for every non-OneTouch person (2026-09-16).
  adminUsersRolesList
    .querySelector<HTMLButtonElement>("#adminAddUser")
    ?.addEventListener("click", () => openAddUserModal(() => void renderAdminUsersRoles()));
}



/**
 * Open one person's dialog. `roleId` names the production grant to edit when a
 * specific chip was clicked; without it the first grant in effect is shown,
 * and for someone with no production grant the dialog opens on their
 * environment access alone (ADR-0045 §6).
 */
function openPerson(personKey: string, roleId?: string): void {
  const person = _catalogue.find((r) => r.key === personKey);
  if (person) openAccessEditor(person, roleId);
}

/** The access editor for one person: as its own dialog (a role chip's
 *  shortcut), or embedded in the person card's Access panel (PR 2b). Takes
 *  the ROW, not a key — a person picked from the shared-address chooser is
 *  a narrowed row that is not itself in the list. */
function openAccessEditor(
  person: CatalogueRow, roleId?: string,
  embed?: { host: HTMLElement; onClose: (changed: boolean) => void },
): void {
  const grant = roleId
    ? (_roleRows.find((r) => r.roleId === roleId) ?? null)
    : (person.productionRoles.find((r) => !r.inactive) ?? person.productionRoles[0] ?? null);
  openEditPractitionerRoleModal({
    row: grant,
    person: {
      name: person.name,
      email: person.email,
      personDocId: person.personDocId,
      environments: person.environments,
      family: person.family,
    },
    onSaved: () => void renderAdminUsersRoles({ quiet: true }),
    embed,
  });
}

/** ADR-0046 §5 (2026-09-22): one card per person, opened from the row. Its
 *  Edit buttons hand over to the existing editors until per-panel editing
 *  lands (PR 2). */
function openCard(personKey: string): void {
  const row = _catalogue.find((r) => r.key === personKey);
  if (!row) return;
  void openPersonCard({
    row,
    keys: _visibleKeys,
    rowByKey: (key) => _catalogue.find((r) => r.key === key),
    rowForContact: (id) => _catalogue.find((r) => r.family.some((l) => l.relatedPersonDocId === id)),
    onEditAccess: (r, embed) => openAccessEditor(r, undefined, embed),
    // A save in the card: reload the list QUIETLY behind the still-open card,
    // then redraw the same person's card in place so the office sees the
    // result (OMON-356: the card no longer closes, nothing blanks). A changed
    // email can change a family-only row's key; then the card closes and the
    // list simply shows them in their new place.
    onSaved: (key) => renderAdminUsersRoles({ quiet: true }).then(() => {
      if (_catalogue.some((r) => r.key === key)) openCard(key);
      else closePersonCard();
    }),
  });
}

function wireRowClicks(bodyEl: HTMLElement): void {
  // A role chip is a shortcut straight to that grant's editor; stopPropagation
  // keeps the row's card handler out of it.
  bodyEl.querySelectorAll<HTMLElement>(".role-chip-link").forEach((el) => {
    el.addEventListener("click", (ev) => {
      ev.stopPropagation();
      const parent = el.closest<HTMLElement>(".admin-row-clickable");
      if (parent?.dataset.personKey) openPerson(parent.dataset.personKey, el.dataset.roleId);
    });
  });
  bodyEl.querySelectorAll<HTMLElement>(".admin-row-clickable").forEach((rowEl) => {
    rowEl.addEventListener("click", () => {
      if (rowEl.dataset.personKey) openCard(rowEl.dataset.personKey);
    });
  });
}

/** The guest list covers every non-production account the invite path minted.
 *  Only the production planner can read it (the callable refuses env-stamped
 *  callers), and a failure must not blank the production list. */
async function loadEnvironmentEntries(): Promise<EnvironmentEntry[]> {
  _environmentsNote = "";
  if (firestoreDatabaseId !== "(default)") {
    _environmentsNote = "Non-production accounts are listed on the production planner only.";
    return [];
  }
  try {
    const data = await callEnvironmentInviteAction({ action: "list", environment: "training" });
    return Array.isArray(data) ? (data as EnvironmentEntry[]) : [];
  } catch (err) {
    console.error("environment guest list failed:", err);
    _environmentsNote = "Couldn't load non-production accounts — showing production only.";
    return [];
  }
}

/** OMON-348: every family contact, so Users & Roles lists everyone. A failure
 *  must not blank the rest of the list — it says what is missing instead. */
async function loadFamilyLinks(): Promise<FamilyLink[]> {
  _familyNote = "";
  try {
    const result = await callRelatedPersonAction({ action: "list-family" });
    return Array.isArray(result?.contacts) ? (result.contacts as FamilyLink[]) : [];
  } catch (err) {
    console.error("family contacts list failed:", err);
    _familyNote = "Couldn't load family contacts — they are missing from this list.";
    return [];
  }
}

/** ADR-0046 §6: the planner's own client list (the one the Clients tab uses,
 *  loaded once and shared), as catalogue input. A failure must not blank the
 *  list — it says what is missing instead. */
async function loadClients(): Promise<ClientInfo[]> {
  _clientsNote = "";
  try {
    await ensurePatients();
    return state.patients.map((p) => ({
      eocId: p.docId, name: p.name, status: p.status, hasPlan: p.hasPlan,
      planVersion: p.planVersion, hasDraft: p.hasDraft,
    }));
  } catch (err) {
    console.error("client list failed:", err);
    _clientsNote = "Couldn't load clients — they are missing from this list.";
    return [];
  }
}

export async function renderAdminUsersRoles(opts: { quiet?: boolean } = {}): Promise<void> {
  // OMON-356: a refresh AFTER A SAVE is quiet — the list on screen stays until
  // the new one is ready. Blanking it to "Loading…" (every person, clients
  // included since #2088) was the blank screen Ed saw behind every card save.
  if (!opts.quiet) {
    const existingBody = adminUsersRolesList.querySelector<HTMLElement>(".admin-users-roles-body");
    if (existingBody) {
      existingBody.innerHTML = `<div class="admin-empty">Loading…</div>`;
    } else {
      adminUsersRolesList.innerHTML = `<div class="admin-empty">Loading…</div>`;
    }
  }

  let roles: PractitionerRoleRow[];
  let entries: EnvironmentEntry[];
  let family: FamilyLink[];
  // 2026-09-24 perf: the client list used to load only AFTER roles and all
  // person reads had finished. It is independent, so it starts now alongside
  // them. loadClients never rejects (it records a note instead), so it can't
  // leave an unhandled rejection if the roles read fails.
  const clientsPromise = loadClients();
  try {
    [roles, entries, family] = await Promise.all(
      [loadPractitionerRoles(), loadEnvironmentEntries(), loadFamilyLinks()]);
  } catch (err) {
    console.error("loadPractitionerRoles failed:", err);
    adminUsersRolesList.innerHTML = `<div class="admin-empty">Failed to load _practitionerRole — check console.</div>`;
    return;
  }

  // One read per person serves the name, the provenance chips and the email
  // the catalogue joins on — batched 30 per query, in parallel (loaders.ts).
  const personIds = roles.map((r) => r.personDocId).filter((id): id is string => id !== null);
  const [summaries, clients] = await Promise.all([loadPersonSummaries(personIds), clientsPromise]);
  _personProvenance = new Map(Array.from(summaries, ([id, s]) => [id, s.provenance] as const));
  for (const r of roles) {
    if (r.personDocId) r.personName = summaries.get(r.personDocId)?.name ?? null;
  }
  const emails = new Map(Array.from(summaries, ([id, s]) => [id, s.email] as const));

  _roleRows = roles;
  const origins = new Map(Array.from(summaries, ([id, s]) => [id, s.provenance?.origin ?? null] as const));
  _catalogue = buildCatalogue(roles, emails, entries, today(), family, origins, clients);
  renderPanelBody();
}
